<template>
  <ProjectSection
    id="lifeos"
    title="LifeOS"
    tagline="A personal operating system for school and daily life."
    chip="Swift 6 · SwiftUI · Hummingbird · SQLite"
    accent="#34d399"
    :icon="['fas', 'calendar-check']"
  >
    <!-- Phone: today view -->
    <article v-reveal class="card span-4 phone-card">
      <h3>One app for school and life</h3>
      <p>An iPhone app backed by my own Swift server on a Mac mini, reached privately over Tailscale.</p>
      <div class="phone visual visual-center visual-bleed">
        <div class="phone-notch"></div>
        <div class="phone-date">Thursday, Oct 15</div>
        <div class="phone-h">Today</div>
        <div v-for="item in today" :key="item.title" class="today-row">
          <span class="today-time">{{ item.time }}</span>
          <span class="today-bar" :style="{ background: item.color }"></span>
          <span class="today-body">
            <strong>{{ item.title }}</strong>
            <small>{{ item.sub }}</small>
          </span>
        </div>
      </div>
    </article>

    <!-- Transaction -->
    <article v-reveal="80" class="card span-8 txn-card">
      <h3>Every change comes with a receipt</h3>
      <p>A command engine writes each change together with its receipt, undo and cause in one SQLite transaction, so retrying a request never applies it twice.</p>
      <div class="txn visual visual-bleed">
        <div class="txn-head">
          <span class="lights"><i></i><i></i><i></i></span>
          <span class="mono">command: complete_assignment</span>
          <span class="badge"><font-awesome-icon :icon="['fas', 'check']" /> idempotent</span>
        </div>
        <div class="txn-body mono">
          <div v-for="(line, i) in txn" :key="i" class="txn-line" :style="{ '--i': i }">
            <span class="ln">{{ i + 1 }}</span>
            <span :class="line.cls">{{ line.text }}</span>
          </div>
        </div>
      </div>
    </article>

    <!-- Integrations -->
    <article v-reveal class="card span-7 merge-card">
      <h3>Four school sites, one data model</h3>
      <p>Canvas, Gradescope, Piazza and Panopto in one place, with duplicate assignments merged and exam dates pulled out of syllabi.</p>
      <div class="merge visual">
        <div class="sources">
          <span v-for="s in sources" :key="s.name" class="source">
            <i :style="{ background: s.color }"></i>{{ s.name }}
          </span>
        </div>
        <svg class="merge-lines" viewBox="0 0 120 200" preserveAspectRatio="none">
          <path v-for="y in [25, 75, 125, 175]" :key="y" :d="`M0 ${y} C 60 ${y}, 60 100, 120 100`" />
        </svg>
        <div class="merged">
          <div class="merged-icon"><font-awesome-icon :icon="['fas', 'calendar-check']" /></div>
          <div class="stat"><strong>10</strong><small>courses</small></div>
          <div class="stat"><strong>1,200+</strong><small>source records</small></div>
        </div>
      </div>
    </article>

    <!-- Gwen -->
    <article v-reveal="80" class="card span-5 gwen-card">
      <h3>Meet Gwen</h3>
      <p>An AI assistant that turns messages into typed actions and remembers what matters.</p>
      <div class="chat visual">
        <div class="bubble me">remind me to start the 424 project friday</div>
        <div class="intent mono">
          <span class="k">create_reminder</span>({<br />
          &nbsp;&nbsp;course: <span class="s">"CMSC424"</span>,<br />
          &nbsp;&nbsp;due: <span class="s">"Fri 5:00 PM"</span><br />
          })
        </div>
        <div class="bubble gwen">
          <span class="gwen-av">G</span>
          Done. I'll ping you Friday afternoon.
        </div>
      </div>
    </article>

    <!-- Live Activity -->
    <article v-reveal class="card span-6 live-card">
      <h3>Leave-by alerts on your Lock Screen</h3>
      <p>Gwen checks Apple Maps traffic and tells you when to leave, with push notifications and Live Activities.</p>
      <div class="lock visual visual-center visual-bleed">
        <div class="lock-time">1:30</div>
        <div class="activity">
          <div class="activity-top">
            <span class="act-icon"><font-awesome-icon :icon="['fas', 'car-side']" /></span>
            <span class="act-text">
              <strong>Leave in 12 min</strong>
              <small>CMSC421 · Iribe Center</small>
            </span>
            <span class="act-eta">14 min<small>drive</small></span>
          </div>
          <div class="act-track"><span></span></div>
        </div>
      </div>
    </article>

    <!-- Evals -->
    <article v-reveal="80" class="card span-6 eval-card">
      <h3>Tested against real conversations</h3>
      <p>An evaluation harness replays real conversations against the live model, so every change is checked before it ships.</p>
      <div class="evals visual">
        <div v-for="e in evals" :key="e.msg" class="eval-row">
          <span class="eval-dot" :class="e.ok ? 'ok' : 'warn'">
            <font-awesome-icon :icon="['fas', e.ok ? 'check' : 'rotate']" />
          </span>
          <span class="eval-msg">"{{ e.msg }}"</span>
          <span class="mono eval-intent">{{ e.intent }}</span>
        </div>
      </div>
    </article>
  </ProjectSection>
</template>

<script>
import ProjectSection from '../ProjectSection.vue';

export default {
  name: 'LifeOSSection',
  components: { ProjectSection },
  data() {
    return {
      today: [
        { time: '9:30', title: 'CMSC421 Lecture', sub: 'Iribe 0324', color: '#34d399' },
        { time: '11:59', title: 'STAT400 HW 6 due', sub: 'Gradescope', color: '#f59e0b' },
        { time: '1:42', title: 'Leave for CMSC424', sub: '14 min drive', color: '#3b82f6' },
        { time: '4:00', title: 'Robotics class', sub: 'Teaching', color: '#8b5cf6' },
      ],
      txn: [
        { text: 'BEGIN IMMEDIATE;', cls: 'kw' },
        { text: 'INSERT INTO changes   …  -- mark HW 6 done', cls: '' },
        { text: 'INSERT INTO receipts  …  -- request id 7f3a', cls: '' },
        { text: 'INSERT INTO undo_log  …  -- how to reverse it', cls: '' },
        { text: 'INSERT INTO events    …  -- why it happened', cls: '' },
        { text: 'COMMIT;', cls: 'ok' },
      ],
      sources: [
        { name: 'Canvas', color: '#ef4444' },
        { name: 'Gradescope', color: '#14b8a6' },
        { name: 'Piazza', color: '#3b82f6' },
        { name: 'Panopto', color: '#22c55e' },
      ],
      evals: [
        { msg: 'what do I have due tomorrow', intent: 'list_due', ok: true },
        { msg: 'move my gym block to 6', intent: 'reschedule', ok: true },
        { msg: 'when should I leave for class', intent: 'leave_by', ok: true },
        { msg: 'remind me after the exam', intent: 'create_reminder', ok: false },
      ],
    };
  },
};
</script>

<style scoped>
/* Phone */
.phone-card { min-height: 520px; }

.phone {
  position: absolute;
  left: 50%;
  bottom: -40px;
  width: 260px;
  height: 330px;
  transform: translateX(-50%);
  padding: 34px 18px 0;
  border-radius: 40px 40px 0 0;
  background: #0b0b0d;
  border: 6px solid #2a2a2f;
  border-bottom: none;
  box-shadow: 0 -10px 60px rgba(52, 211, 153, 0.12);
}

.phone-notch {
  position: absolute;
  top: 10px;
  left: 50%;
  width: 80px;
  height: 22px;
  transform: translateX(-50%);
  border-radius: 20px;
  background: #000;
}

.phone-date { font-size: 11px; color: var(--text-faint); text-transform: uppercase; letter-spacing: 0.06em; }
.phone-h { font-size: 24px; font-weight: 700; margin: 2px 0 14px; }

.today-row {
  display: grid;
  grid-template-columns: 36px 3px 1fr;
  gap: 10px;
  align-items: center;
  padding: 8px 0;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
}

.today-time { font-size: 12px; color: var(--text-dim); }
.today-bar { height: 28px; border-radius: 2px; }
.today-body strong { display: block; font-size: 13px; font-weight: 500; }
.today-body small { font-size: 11px; color: var(--text-faint); }

/* Transaction */
.txn {
  position: absolute;
  left: 28px;
  right: -20px;
  bottom: -20px;
  border-radius: 16px 0 0 0;
  background: #111114;
  border: 1px solid var(--line-strong);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
}

.txn-head {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 16px;
  border-bottom: 1px solid var(--line);
  color: var(--text-dim);
}

.lights { display: flex; gap: 6px; flex: none; }
.txn-head .mono { min-width: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.lights i { width: 10px; height: 10px; border-radius: 50%; background: #3a3a40; }

.badge {
  margin-left: auto;
  margin-right: 20px;
  flex: none;
  padding: 4px 10px;
  border-radius: 999px;
  background: rgba(52, 211, 153, 0.12);
  color: var(--lifeos);
  font-size: 12px;
  font-weight: 500;
}

.txn-body { padding: 14px 0 34px; }

.txn-line {
  display: flex;
  gap: 18px;
  padding: 3px 16px;
  color: #c8c8d0;
  opacity: 0;
  transform: translateX(-8px);
  transition: opacity 0.5s, transform 0.5s var(--ease);
  transition-delay: calc(var(--i) * 140ms + 300ms);
}

.reveal.is-visible .txn-line { opacity: 1; transform: none; }
.ln { color: var(--text-faint); width: 12px; text-align: right; }
.kw { color: #c4b5fd; }
.ok { color: var(--lifeos); }

/* Merge */
.merge {
  position: absolute;
  left: 28px;
  right: 28px;
  bottom: 30px;
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  height: 200px;
}

.sources { display: flex; flex-direction: column; justify-content: space-between; height: 100%; padding: 4px 0; }

.source {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 9px 14px;
  border-radius: 12px;
  background: #121215;
  border: 1px solid var(--line);
  font-size: 14px;
}

.source i { width: 8px; height: 8px; border-radius: 50%; }

.merge-lines { width: 100%; height: 100%; }
.merge-lines path {
  fill: none;
  stroke: rgba(52, 211, 153, 0.45);
  stroke-width: 1.5;
  stroke-dasharray: 4 6;
  vector-effect: non-scaling-stroke;
  animation: flow 1.6s linear infinite;
}

@keyframes flow { to { stroke-dashoffset: -20; } }

.merged {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 18px 22px;
  border-radius: 20px;
  background: #121215;
  border: 1px solid rgba(52, 211, 153, 0.3);
  box-shadow: 0 0 40px rgba(52, 211, 153, 0.12);
}

.merged-icon {
  display: grid;
  place-items: center;
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: linear-gradient(160deg, #6ee7b7, #059669);
  font-size: 20px;
}

.stat { text-align: center; }
.stat strong { display: block; font-size: 22px; font-weight: 700; }
.stat small { font-size: 12px; color: var(--text-dim); }

/* Gwen */
.chat {
  position: absolute;
  left: 28px;
  right: 28px;
  bottom: 28px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.bubble {
  max-width: 85%;
  padding: 11px 15px;
  border-radius: 18px;
  font-size: 14px;
  line-height: 1.4;
}

.bubble.me {
  align-self: flex-end;
  background: #2563eb;
  border-bottom-right-radius: 6px;
}

.bubble.gwen {
  display: flex;
  align-items: center;
  gap: 10px;
  background: #2a2a2f;
  border-bottom-left-radius: 6px;
}

.gwen-av {
  display: grid;
  place-items: center;
  flex: none;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: linear-gradient(160deg, #6ee7b7, #059669);
  font-size: 12px;
  font-weight: 700;
}

.intent {
  align-self: flex-start;
  padding: 10px 14px;
  border-radius: 12px;
  border: 1px dashed rgba(52, 211, 153, 0.4);
  background: rgba(52, 211, 153, 0.06);
  color: #d4d4d8;
  font-size: 12px;
  line-height: 1.6;
}

.k { color: var(--lifeos); }
.s { color: #fcd34d; }

/* Live activity */
.lock {
  position: absolute;
  left: 50%;
  bottom: -30px;
  width: min(420px, 90%);
  transform: translateX(-50%);
  padding: 22px 16px 50px;
  border-radius: 34px 34px 0 0;
  background: radial-gradient(circle at 30% 0%, #1f3a35, #0f1513 70%);
  border: 1px solid var(--line-strong);
  border-bottom: none;
  text-align: center;
}

.lock-time {
  font-size: 64px;
  font-weight: 700;
  letter-spacing: -0.03em;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 18px;
}

.activity {
  padding: 14px;
  border-radius: 22px;
  background: rgba(20, 20, 22, 0.75);
  backdrop-filter: blur(12px);
  text-align: left;
}

.activity-top { display: flex; align-items: center; gap: 12px; }

.act-icon {
  display: grid;
  place-items: center;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: var(--lifeos);
  color: #062b1f;
}

.act-text strong { display: block; font-size: 15px; }
.act-text small { font-size: 12px; color: var(--text-dim); }

.act-eta {
  margin-left: auto;
  text-align: right;
  font-size: 18px;
  font-weight: 700;
  color: var(--lifeos);
}

.act-eta small { display: block; font-size: 11px; font-weight: 400; color: var(--text-dim); }

.act-track {
  height: 5px;
  margin-top: 12px;
  border-radius: 5px;
  background: rgba(255, 255, 255, 0.1);
  overflow: hidden;
}

.act-track span {
  display: block;
  width: 72%;
  height: 100%;
  border-radius: inherit;
  background: var(--lifeos);
}

/* Evals */
.evals {
  position: absolute;
  left: 28px;
  right: 28px;
  bottom: 28px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.eval-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 11px 14px;
  border-radius: 14px;
  background: #121215;
  border: 1px solid var(--line);
  font-size: 14px;
}

.eval-dot {
  display: grid;
  place-items: center;
  flex: none;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  font-size: 11px;
}

.eval-dot.ok { background: rgba(52, 211, 153, 0.15); color: var(--lifeos); }
.eval-dot.warn { background: rgba(245, 158, 11, 0.15); color: var(--quant); }
.eval-msg { color: #d4d4d8; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.eval-intent { margin-left: auto; color: var(--text-faint); font-size: 12px; white-space: nowrap; }

@media (max-width: 900px) {
  .phone-card { min-height: 500px; }
  .txn-card { min-height: 440px; }
  .merge-card, .gwen-card, .live-card, .eval-card { min-height: 440px; }
}
</style>
